import { QueryClient, useQueryClient } from "@tanstack/react-query"
import eq from "lodash-es/eq"
import every from "lodash-es/every"
import includes from "lodash-es/includes"
import isEmpty from "lodash-es/isEmpty"
import isNumber from "lodash-es/isNumber"
import { useEffectOnce, useMount } from "react-use"

import { reduxBatch } from "@/3514/utils"

import { ECoachingChatQueryKey as EQueryKey } from "../config"
import {
    useCoachingChatConversationDispatcher as useConversationDispatcher,
    useCoachingChatConversationIdSelector as useConversationIdSelector,
    useCoachingChatConversationStatusSelector as useConversationStatusSelector,
    useCoachingChatFormDispatcher as useFormDispatcher
} from "../store"
import {
    ECoachingChatConversationStatus as EConversationStatus,
    type ICoachingChatConversationQueryDto,
    type TCoachingChatConfig as TChatConfig,
    type TCoachingChatSeedQuestion as TSeedQuestion,
    type _TCoachingChatHookSharedParams as _TSharedParams
} from "../types"
import { coachingChatUiUtils as uiUtils } from "../utils"
import { logCoachingChatState as logChatState } from "../utils/coaching-chat.diagnostics"

const {
    formExtractors: { mapSeedQuestionsToFormFields },
    tabId: { createAndSave: createAndSaveTabId, get: getTabId }
} = uiUtils

type _TParams = Pick<_TSharedParams, "entityId" | "wasChatCompleted"> &
    Pick<TChatConfig, "conversationId" | "seedQuestions" | "isChatAvailable">

function useCoachingChatInitialLoader({
    entityId,
    wasChatCompleted,
    conversationId: existingConversationId,
    seedQuestions,
    isChatAvailable
}: _TParams): void {
    const qc: QueryClient = useQueryClient()

    const formDispatcher: ReturnType<typeof useFormDispatcher> = useFormDispatcher(entityId)

    const conversationDispatcher: ReturnType<typeof useConversationDispatcher> = useConversationDispatcher(entityId)

    // The chat's own store survives SPA navigation (nothing clears it), so on a re-entry it can
    // already know about a conversation the freshly-read chapter payload has not caught up with.
    const storedConversationId: number | null = useConversationIdSelector(entityId)

    const storedStatus: EConversationStatus = useConversationStatusSelector(entityId)

    useMount((): void => {
        if (!isEmpty(getTabId())) return

        createAndSaveTabId()
    })

    useEffectOnce((): void => {
        // A conversation counts as created if either source knows about it. Trusting the chapter
        // payload alone downgrades a live chat to `Locked` on re-entry, because the payload is not
        // refreshed when a conversation is created mid-session — and `Locked` is not a joinable
        // status, so the socket never asks for history and the feed comes back empty.
        const _wasCreated: boolean = isNumber(existingConversationId) || isNumber(storedConversationId)
        const _wereSeedsDone: boolean = every<TSeedQuestion>(
            seedQuestions,
            (s: TSeedQuestion): boolean => !isEmpty(s.answer)
        )

        // Completion is judged from BOTH sources, and the store wins. The chapter payload cannot
        // be trusted here even right after a refetch: the backend writes `completed_stamp` *after*
        // it has already streamed the summary to this client, so `is_chat_completed` can read
        // false for a conversation this very session watched finish. The store set `Completed`
        // itself and survives SPA navigation — it is the authoritative witness.
        const _wasStoreCompleted: boolean = includes<EConversationStatus>(
            [EConversationStatus.GeneratingSummary, EConversationStatus.Completed],
            storedStatus
        )
        const _isCompleted: boolean = wasChatCompleted || _wasStoreCompleted

        // This runs exactly once, against whatever the chapter query holds right now. If that
        // payload is stale (finishing a chat does not refresh ChapterById for the current
        // chapter), every decision below is made on stale input and never revisited. A
        // `wasChatCompleted: false` here for a chat the participant has already finished is the
        // fingerprint of that.
        logChatState("init", {
            entityId,
            wasChatCompleted,
            storedStatus,
            decidedCompleted: _isCompleted,
            isChatAvailable,
            conversationId: existingConversationId,
            storedConversationId,
            wasConversationCreated: _wasCreated,
            seedsCount: seedQuestions?.length ?? 0,
            wereSeedsDone: _wereSeedsDone
        })

        reduxBatch((): void => {
            if (!isEmpty(seedQuestions)) formDispatcher.loadSeeds(mapSeedQuestionsToFormFields(seedQuestions))

            if (_isCompleted && isChatAvailable) {
                formDispatcher.submitForm()
                conversationDispatcher.setConversationId(existingConversationId ?? storedConversationId)
                if (!_wasStoreCompleted) conversationDispatcher.changeStatus(EConversationStatus.Completed)
            } else if (!_isCompleted && isChatAvailable) {
                if (!_wasCreated && !_wereSeedsDone) conversationDispatcher.changeStatus(EConversationStatus.Locked)

                if (_wasCreated || _wereSeedsDone) {
                    if (!_wasCreated && _wereSeedsDone)
                        conversationDispatcher.changeStatus(EConversationStatus.ReadyToStart)

                    if (_wasCreated && _wereSeedsDone) {
                        conversationDispatcher.setConversationId(existingConversationId)
                        conversationDispatcher.changeStatus(EConversationStatus.Ready)
                        formDispatcher.submitForm()
                    }
                }

                if (isChatAvailable) {
                    const _chatCachedData: ICoachingChatConversationQueryDto = qc.getQueryData(
                        EQueryKey.Conversation(existingConversationId)
                    )

                    if (!isEmpty(_chatCachedData)) {
                        if (eq(_chatCachedData.status, "in_progress"))
                            conversationDispatcher.changeStatus(EConversationStatus.Ready)

                        if (eq(_chatCachedData.status, "completed"))
                            conversationDispatcher.changeStatus(EConversationStatus.Completed)
                    }
                }
            } else if (!isChatAvailable) if (_wasCreated) formDispatcher.submitForm()
        })
    })
}

export { useCoachingChatInitialLoader }
